/**
 * closingSeed.js — the printed closing sheets, used to seed the Closing walk-through.
 *
 * Three-level tree: Section → Sub-section → Items. `target` (a Today list id) flows DOWN
 * the tree — an item uses its own `target`, else its sub-section's, else its section's.
 * Items are strings (inherit) or { name, target?, note? }. Seeded once from the printed
 * sheets; fully editable in the app (add / rename / delete / reorder) at every level.
 * Sub-sections mirror the sheet headers:
 *   • Middle Man Supplies = "Supplies" (→To-Go) + "Daily To-Do" (→Prep)
 *   • Signature Sauces    = "Sauces" (→Cooking) + "Indo-Chinese" (→Groceries) + "Fridge Bottom Inventory" (→Prep)
 *   • Chat Section        = "Fridge" (→Prep) + "Top" (→Cooking)
 * Every item's resolved target matches `suggestCategory()` (locked by closingSeed.test.js),
 * so the in-app "Sort into lists" review finds nothing to change on a fresh seed.
 */
export const CLOSING_SEED = [
  {
    title: 'Middle Man Supplies', icon: '🥡', target: 'supplies',
    subs: [
      {
        title: 'Supplies', target: 'supplies',
        items: [
          'To-go Boxes – Black/White', 'Daily Containers – Transparent', 'Dosa & Idly Boxes',
          'Gloves, Deli Sheets & Silver Foil', 'Ketchup', 'Plates', 'Water Bottles',
          'T-Shirt Bags', 'Paper Napkins & Cutlery', 'Coffee & Tea Cups', 'Chutney & Sambar Cups with Lids',
        ],
      },
      {
        title: 'Daily To-Do', target: 'prep',
        items: [
          'Onion Refill', 'Sambar Refill & Quality Check', { name: 'Red Chutney', target: 'cooking' }, 'Raita', 'Mango Lassi',
          { name: 'Cold Drinks', target: 'groceries' }, { name: 'Kulfi (Mango & Pista)', target: 'groceries' },
          { name: 'Dollar/Coin Check', target: 'cleaning' }, { name: 'End of Day Cleaning', target: 'cleaning' },
        ],
      },
    ],
  },
  {
    title: 'Signature Sauces', icon: '🥫', target: 'cooking',
    subs: [
      {
        title: 'Sauces', target: 'cooking',
        items: [
          { name: 'Vijayawada Sauce', note: '1/6' }, { name: '65 Sauce', note: '1/6' },
          { name: '555 Sauce', note: '1/6' }, { name: 'Manchurian Sauce', note: '1/6' },
          { name: 'Chili Sauce', note: '1/6' }, { name: 'DB Sauce', note: '1/6' },
          { name: 'Mint', note: '1/2', target: 'prep' }, { name: 'Cilantro', note: '1/2', target: 'prep' },
          { name: 'Chopped Chilies', note: '1/2', target: 'prep' },
        ],
      },
      {
        title: 'Indo-Chinese', target: 'groceries',
        items: [
          'Green Chili Sauce', 'Soy Sauce', 'Red Chili Sauce', 'Hot & Sweet Chili Sauce', 'Vinegar', 'Eggs',
          { name: 'Salt', target: 'chef' },
        ],
      },
      {
        title: 'Fridge Bottom Inventory', target: 'prep',
        items: [
          { name: 'Mutton Pieces', note: '1/6' }, { name: 'Mutton Gravy', note: '1/6', target: 'cooking' },
          { name: 'Cooked Chicken Pieces', note: '1/6', target: 'cooking' }, { name: 'Fried Rice', note: '1/3', target: 'cooking' },
          { name: 'Bell Peppers / Onion', note: '1/6' }, { name: 'Fried Rice Veggies', note: '1/3' },
          { name: 'Spring Onions', note: '1/12' }, { name: 'Chopped Garlic', note: '1/12' },
          { name: 'Shrimp', note: '1/3' }, { name: 'Carrot & Peas', note: '1/2' },
          { name: 'Gongura', target: 'cooking' }, { name: 'Whipping Cream', target: 'groceries' }, 'Poori',
          { name: 'Noodles', note: '1/3' }, { name: 'Methi Sauce', note: '1/3', target: 'cooking' },
          { name: 'Chopped Ginger', note: '1/12' }, 'Raju Gari Chicken Pcs',
          { name: 'Garlic & Butter Naans', target: 'groceries' },
        ],
      },
    ],
  },
  {
    title: 'Chat Section', icon: '🍲', target: 'cooking',
    subs: [
      {
        title: 'Fridge', target: 'prep',
        items: [
          'Dosa Batters', { name: 'Naans / Paratha', target: 'groceries' }, 'Curry Refill', 'Pani Poori Water',
          { name: 'Gulab Jamun', target: 'groceries' }, { name: 'Red Chutney', target: 'cooking' }, 'Chinese Refills',
          { name: 'Chaat Sauces', target: 'cooking' },
        ],
      },
      {
        title: 'Top', target: 'cooking',
        items: [
          { name: 'Raita & Sweet Curd', note: '1/3', target: 'prep' }, { name: 'Chicken Curry', note: '1/2' },
          { name: 'Boneless Chicken Curry', note: '1/2' }, { name: 'Sautéed Veggies', note: '1/2' },
          { name: 'Lamb Pieces', note: '1/2', target: 'prep' }, { name: 'Tikka Sauce', note: '1/3' },
          { name: 'Chicken Tikka Pieces', note: '1/2', target: 'prep' }, { name: 'Veg Keema', note: '1/2' },
          { name: 'Veg Kurma', note: '1/6' }, { name: 'Bhel Poori', target: 'prep' }, { name: 'Papdi', target: 'groceries' },
        ],
      },
    ],
  },
];

/**
 * Closing item names per Today list (item → sub-section → section target), in walk
 * order. The built-in lists add these to their suggestions, so the closing walk-through
 * and the daily board share one vocabulary (see config/lists.js).
 * @returns {{ [listId]: string[] }}
 */
export function closingNamesByList(seed = CLOSING_SEED) {
  const out = {};
  seed.forEach(sec => (sec.subs || []).forEach(sub => (sub.items || []).forEach(raw => {
    const it = typeof raw === 'string' ? { name: raw } : raw;
    const id = it.target || sub.target || sec.target;
    (out[id] = out[id] || []).push(it.name);
  })));
  return out;
}
